import type { Batch, BatchFieldWithValue, Document } from '~/common/model/api/batch';
import type { ValidateFieldsRequestData } from '~/services/server/api/batch';

/**
 * Create Batch ValidationFields.
 *
 * @param {Batch} batch Batch.
 * @param {Document | undefined} document Document.
 * @param {BatchFieldWithValue} field Batch field.
 *
 * @returns {ValidateFieldsRequestData['validationFields']} Batch ValidationFields.
 */
export function createBatchValidationFields(
  batch: Batch,
  document: Document | undefined,
  field: BatchFieldWithValue,
): ValidateFieldsRequestData['validationFields'] {
  const batchFields = [
    ...new Set([
      // Current field
      field.id,
      // List dependent batch fields
      ...field.dependent.fields,
    ]),
  ];

  const targetDocumentType = document ? batch.type.documentTypes?.find((documentType) => documentType.name === document.type) : undefined;
  if (!targetDocumentType) {
    return {
      batchFields,
      documentFields: [],
    };
  }

  const documentFields = [
    ...new Set(
      // List dependent document fields
      field.dependent.documents.find((dependentDocumentType) => dependentDocumentType.id === targetDocumentType.id)?.fields,
    ),
  ];
  return {
    batchFields,
    documentFields,
  };
}

/**
 * Create Document ValidationFields.
 *
 * @param {Batch} batch Batch.
 * @param {Document} document Document.
 * @param {BatchFieldWithValue} field Document field.
 *
 * @returns {ValidateFieldsRequestData['validationFields']} Document ValidationFields.
 */
export function createDocumentValidationFields(batch: Batch, document: Document, field: BatchFieldWithValue): ValidateFieldsRequestData['validationFields'] {
  const batchFields = [
    ...new Set(
      // List dependent batch fields
      field.dependent.fields,
    ),
  ];

  const targetDocumentType = batch.type.documentTypes?.find((documentType) => documentType.name === document.type);
  if (!targetDocumentType) {
    return {
      batchFields,
      documentFields: [],
    };
  }

  const documentFields = [
    ...new Set([
      // Current field
      field.id,
      // List dependent document fields
      ...(field.dependent.documents.find((dependentDocumentType) => dependentDocumentType.id === targetDocumentType.id)?.fields ?? []),
    ]),
  ];
  return {
    batchFields,
    documentFields,
  };
}
